import { useSaveData } from '@entities/common/savedData/index.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useArticleEditorPageRedirect } from '@widgets/articles/article-edit/helpers.ts';
import { EditorPartitions } from '@widgets/programs/index.ts';
import { useDescriptionEditor } from '@widgets/shared/descriptionEditor/model.ts';
import { FormDescriptionEditor } from '@widgets/shared/Forms/DescriptionEditor/index.tsx';
import { PageBackButton } from '@features/common/PageBackButton/index.tsx';
import { useAppDispatch } from '@shared/hooks';
import { useErrorsHandler } from '@shared/hooks/use-errors-handler.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { usePartitionsWithTagsSelection } from '@shared/hooks/use-partitions-with-tags-selection.ts';
import { useCloseFormDialogModal } from '@shared/hooks/useCloseFormDialogModal/useCloseFormDialogModal.ts';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import type { ArticleEditValues } from '@shared/types/articles';
import { FormImageAndName, ListBody, PageHead, PageTitle, TextButton } from '@shared/ui';
import { FormCommentsField } from '@shared/ui/forms/CommentsField/index.tsx';
import { CreateAndEditArticle } from '@shared/yupValidators/validators.ts';
import { useArticleQuery } from '@src/api/api-hooks/articles/useArticleQuery.ts';
import { useCreateArticleMutation } from '@src/api/api-hooks/articles/useCreateArticleMutation.ts';
import { useUpdateArticleMutation } from '@src/api/api-hooks/articles/useUpdateArticleMutation.ts';
import { Loader } from '@src/library/components/common/index.ts';
import { showPopup } from '@src/library/redux/modal';

export const ArticleEdit = () => {
  const { articleId } = useParams();
  const dispatch = useAppDispatch();
  const { moduleId, code } = useModuleByCodeParam();
  const { doRedirectOnPrevPage } = useArticleEditorPageRedirect();
  const _HASH = useMemo(() => `ArticleEditorPage${articleId}`, [articleId]);
  const { handleErrors } = useErrorsHandler();
  const { doCloseFormWithDialogModal } = useCloseFormDialogModal();

  const { clearStorage: clearDescription } = useDescriptionEditor(_HASH);

  const {
    data: savedData,
    doSetStorageData,
    doRemoveStorageData,
  } = useSaveData<ArticleEditValues>('ArticlesForm');

  const { mutateAsync: createArticle, isPending: isCreatingPending } = useCreateArticleMutation();
  const { mutateAsync: updateArticle, isPending: isUpdatingPending } = useUpdateArticleMutation();

  const { data: dataFromRtk, isLoading } = useArticleQuery({
    id: articleId === 'new' ? undefined : articleId,
  });

  const { clearPartitionsData, selectedPartitions, selectedTags } = usePartitionsWithTagsSelection({
    hash: _HASH,
    initializeData: {
      tags: dataFromRtk?.tags || [],
      partitions: dataFromRtk?.partitions || [],
      savedFormState: savedData,
    },
  });

  const articleParsedData = useMemo(() => {
    if (dataFromRtk) {
      return {
        ...dataFromRtk,
        tags: dataFromRtk?.tags.map((tag) => tag.id),
        partitions: dataFromRtk?.partitions.map((partition) => partition.id),
      };
    }
    return undefined;
  }, [dataFromRtk]);

  const defaultFormValues = useMemo(
    () =>
      (savedData && { ...savedData, partitions: selectedPartitions, tags: selectedTags }) ||
      articleParsedData || {
        content: '',
        cover: '',
        description: '',
        module: moduleId,
        tags: selectedTags,
        partitions: selectedPartitions,
        title: '',
        access_duration: 1,
        with_comments: false,
        with_likes: true,
      },
    [savedData, articleParsedData, selectedPartitions, selectedTags, moduleId]
  );
  const formMethods = useForm<ArticleEditValues>({
    resolver: yupResolver(CreateAndEditArticle),
    defaultValues: defaultFormValues,
    values: defaultFormValues,
  });

  const {
    setValue,
    getValues,
    formState: { errors, dirtyFields },
    handleSubmit,
    clearErrors,
    setError,
  } = formMethods;

  const closeFrom = useCallback(() => {
    doRedirectOnPrevPage(`/articles/${code}`);
    setTimeout(() => {
      clearPartitionsData();
      clearDescription();
      doRemoveStorageData();
    }, 5);
  }, [doRedirectOnPrevPage, clearPartitionsData, clearDescription, doRemoveStorageData, code]);

  const onSubmit: SubmitHandler<ArticleEditValues> = async (data) => {
    const isCreating = articleId === 'new';
    try {
      isCreating ? await createArticle(data) : await updateArticle({ ...data, id: articleId });
      closeFrom();
      dispatch(
        showPopup({
          type: 'success',
          text: isCreating ? 'Статья успешно создана' : 'Изменения успешно внесены в статью',
        })
      );
    } catch (e: unknown) {
      handleErrors({ e, setError });
    }
  };
  return (
    <>
      <PageTitle title={articleId === 'new' ? 'Новая статья' : 'Редактирование статьи'} />
      <PageHead
        Left={
          <PageBackButton
            onClick={doCloseFormWithDialogModal({
              isShouldModalOpen: Boolean(
                Object.keys(dirtyFields).length
                // || selectedIds?.length
              ),
              onModalConfirm: closeFrom,
            })}
          />
        }
        Right={
          <TextButton
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isCreatingPending || isUpdatingPending}
          >
            Готово
          </TextButton>
        }
      />
      <ListBody>
        {isLoading ? (
          <Loader />
        ) : (
          <form className={shared.forFormsGap20}>
            <FormImageAndName
              imageError={errors.cover?.message}
              titleError={errors.title?.message}
              imageValue={defaultFormValues.cover}
              titleValue={defaultFormValues.title}
              onImageChange={(photo) => {
                clearErrors('cover');
                setValue('cover', photo, {
                  shouldDirty: photo !== '',
                });
              }}
              onTitleChange={(title) => {
                clearErrors('title');
                setValue('title', title, {
                  shouldDirty: title !== '',
                });
              }}
              placeholder="Название"
              required
            />

            <FormDescriptionEditor
              descriptionPageTitle="Статья"
              fieldLabel="статья"
              hash={_HASH}
              onChange={(v) => setValue('content', v, { shouldDirty: v !== '' })}
              onRedirect={() => doSetStorageData(getValues())}
              initData={defaultFormValues.content}
              error={errors?.content}
            />

            <EditorPartitions onRedirect={() => doSetStorageData(getValues())} hash={_HASH} />

            <FormCommentsField
              initData={defaultFormValues?.with_comments || false}
              onChange={(v) => setValue('with_comments', v, { shouldDirty: v })}
            />
          </form>
        )}
      </ListBody>
    </>
  );
};
